import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Paper } from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCarTwoTone";
import { Add as AddIcon } from "@mui/icons-material";
import { Typography, Button, Box, Stack, CircularProgress } from "@financeable-com-au/financeable-ui";
import { APPLICATION_SECTIONS_ID, ERRORS_MESSAGE } from "@/constants";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { IDispatchResponse } from "@/utils/interface";
import { ApplicationType, Asset } from "@/store/slices/types/applicationFormSlice";
import { UserSlice, UserState } from "@/store/slices/types/userSlice";
// @ts-expect-error: ignoring as it is existing js file
import { userSelector } from "@/store/slices/userSlice";
import AssetSections from "./AssetSections";
import { useAutoInsuranceQuote } from "./useAutoInsuranceQuote";
import {
  assetSelector,
  addAdditionalAsset,
  userDetailsSelector,
  setRemoveAssetModalOptions,
  removeAdditionalAsset,
  // @ts-expect-error: ignoring as it is existing js file
} from "../../store/slices/applicationFormSlice";
// @ts-expect-error: ConfirmModal is a JS file without type definitions
import ConfirmModal from "../customComponents/ConfirmModal";

type AssetSectionsProps = {
  applicationType: ApplicationType;
  applicationId: string;
  hideAdvancedActions?: boolean;
};

const AssetList = React.memo(({ applicationType, applicationId, hideAdvancedActions = false }: AssetSectionsProps) => {
  const dispatch = useDispatch();
  const asset = useSelector(assetSelector);
  const { permissions } = useSelector<UserSlice, UserState>(userSelector);
  const userDetails =
    (useSelector(userDetailsSelector) as {
      application?: {
        additionalAssets?: Array<{ _id: string;[key: string]: unknown }>;
        [key: string]: unknown;
      };
      gettingAdditionalAsset?: boolean;
      removeAssetModalOptions?: {
        isOpen: boolean;
        assetType: string;
        assetId: string;
        isRemovingAsset?: boolean;
      };
    }) || {};
  const application = userDetails?.application;
  const { gettingAdditionalAsset, removeAssetModalOptions } = userDetails;
  const isDisableApplicationEdit = !permissions?.application?.edit || !application?.canEditApplication;
  const [assetList, setAssetList] = useState<Array<{ _id: string;[key: string]: unknown }>>([]);
  useAutoInsuranceQuote({ applicationId, applicationType, hideAdvancedActions });

  useEffect(() => {
    if (asset && typeof asset === "object" && "_id" in asset) {
      const totalAssets = [asset];

      if (application?.additionalAssets && application.additionalAssets.length > 0) {
        totalAssets.push(...application.additionalAssets);
      }

      // Fix type: ensure all _id are string via mapping/casting
      setAssetList(
        totalAssets.map((a) => ({
          ...a,
          _id: String((a as { _id: unknown })._id),
        })) as Array<{ _id: string;[key: string]: unknown }>,
      );
    }
  }, [asset, application?.additionalAssets]);

  const handle = {
    addAdditionalAsset: () => {
      dispatch(addAdditionalAsset({ applicationId })).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
              intent: "danger",
            }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.status_message || "Additional asset added successfully",
              intent: "success",
            }),
          );
        }
      });
    },
    closeRemoveAssetModal: () => {
      dispatch(
        setRemoveAssetModalOptions({
          isOpen: false,
          assetType: "",
          assetId: "",
        }),
      );
    },
    removeAsset: async (data: { isSave: boolean }) => {
      if (data.isSave) {
        // Remove asset from application
        dispatch(removeAdditionalAsset({ assetId: removeAssetModalOptions?.assetId, applicationId })).then(
          (res: IDispatchResponse) => {
            if (res?.error) {
              dispatch(
                openSnackbar({
                  message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
                  intent: "danger",
                }),
              );
            } else {
              dispatch(
                openSnackbar({
                  message: res?.payload?.status_message || "Asset removed successfully",
                  intent: "success",
                }),
              );
            }
            // Close modal after removing asset
            handle.closeRemoveAssetModal();
          },
        );
      }
    },
  };

  return (
    <>
      <Grid
        container
        id={APPLICATION_SECTIONS_ID.ASSETS}
        style={{
          margin: "0 0 30px",
          padding: "0 0 20px",
          borderBottom: "2px solid rgba(0,0,0,0.12)",
        }}
      >
        <Paper elevation={0} sx={{ borderRadius: 2, width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              alignItems: "center",
            }}
          >
            <Stack direction="row" style={{ marginBottom: "10px", padding: "0 10px" }}>
              <DirectionsCarIcon style={{ marginRight: "10px" }} />
              <Typography level="body-lg">Assets</Typography>
            </Stack>
            <Button variant="secondary" size="md" onClick={handle.addAdditionalAsset} disabled={gettingAdditionalAsset || isDisableApplicationEdit}>
              {gettingAdditionalAsset ? (
                <CircularProgress size="sm" style={{ marginRight: "5px" }} />
              ) : (
                <AddIcon fontSize="small" />
              )}
              {gettingAdditionalAsset ? "Adding..." : "Add Asset"}
            </Button>
          </Box>

          <Paper elevation={0} sx={{ border: "none", borderRadius: 5, width: "100%", mt: 2 }}>
            {assetList &&
              assetList.length > 0 &&
              assetList.map((asset, index) => (
                <AssetSections
                  key={asset._id}
                  index={index}
                  isDisableEdit={isDisableApplicationEdit}
                  asset={asset as unknown as Asset}
                  applicationType={applicationType}
                  applicationId={applicationId}
                  hideAdvancedActions={hideAdvancedActions}
                />
              ))}
          </Paper>
        </Paper>
      </Grid>

      <ConfirmModal
        isOpen={removeAssetModalOptions?.isOpen}
        handle={handle.removeAsset}
        setIsOpen={handle.closeRemoveAssetModal}
        title={"Confirm"}
        btnTextNo="No"
        btnTextYes="Yes"
        confirmLoadingText="Removing..."
        message={`You’re about to remove asset ${removeAssetModalOptions?.assetType ? `“${removeAssetModalOptions.assetType}”` : ""}. Are you sure?`}
        isConfirmLoading={removeAssetModalOptions?.isRemovingAsset}
      />
    </>
  );
});

export default AssetList;
